<template>

  <div v-if="isUpdating" class="update-overlay">
    <div class="update-loader">
      <div class="loader-spinner"></div>
      <p class="loader-text">Обновляем приложение...</p>
    </div>
  </div>


  <div v-else-if="showUpdate" class="pwa-update-notification">
    <div class="pwa-update-content">
      <p class="pwa-update-text">Доступна новая версия приложения</p>
      <div class="pwa-update-buttons">
        <button @click="updateApp" class="pwa-update-btn update">Обновить</button>
        <button @click="closeNotification" class="pwa-update-btn later">Позже</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'

const showUpdate = ref(false)
const registration = ref(null)
const isUpdating = ref(false)

const onSWUpdate = (event) => {
  console.log('🔥 Доступно обновление!', event)
  registration.value = event.detail
  showUpdate.value = true
}

const updateApp = async () => {
  if (isUpdating.value) return
  
  console.log('🔄 Обновление приложения...')
  showUpdate.value = false
  isUpdating.value = true
  
  if (registration.value && registration.value.waiting) {
    registration.value.waiting.postMessage({ type: 'SKIP_WAITING' })
    
    setTimeout(async () => {
      // Способ 1: Полная очистка кэша перед перезагрузкой
      if ('caches' in window) {
        const cacheNames = await caches.keys()
        await Promise.all(cacheNames.map(name => caches.delete(name)))
        console.log('✅ Кэш очищен')
      }

      const url = new URL(window.location.href)
      url.searchParams.set('_t', Date.now())
      
      
      document.location.replace(url.toString())
    }, 1500)
  } else {
    
    if ('caches' in window) {
      const cacheNames = await caches.keys()
      await Promise.all(cacheNames.map(name => caches.delete(name)))
    }
    document.location.reload(true)
  }
}

const closeNotification = () => {
  console.log('❌ Уведомление закрыто')
  showUpdate.value = false
}

onMounted(() => {
  console.log('📋 PWAUpdate компонент загружен')
  
  document.addEventListener('swUpdated', onSWUpdate)
  
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then(reg => {
      if (reg.waiting) {
        console.log('⏳ Найден ожидающий SW при загрузке')
        registration.value = reg
        showUpdate.value = true
      }
    })
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('swUpdated', onSWUpdate)
})
</script>

<style scoped>
/* Затемненный фон с индикатором */
.update-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease;
}

.update-loader {
  background: var(--bg-primary);
  padding: 30px 40px;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  text-align: center;
  animation: scaleIn 0.3s ease;
}

.loader-spinner {
  width: 50px;
  height: 50px;
  border: 3px solid var(--border-light);
  border-top: 3px solid var(--btn-accent-bg);
  border-radius: 50%;
  margin: 0 auto 15px;
  animation: spin 1s linear infinite;
}

.loader-text {
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  animation: pulse 1.5s ease infinite;
}

/* Стили для уведомления */
.pwa-update-notification {
  position: fixed;
  bottom: 20px;
  left: 20px;
  right: 20px;
  background: var(--bg-primary);
  border-radius: var(--border-radius-md);
  padding: 16px 20px;
  z-index: 9999;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  animation: slideUp 0.3s var(--transition-fast);
}

@media (min-width: 640px) {
  .pwa-update-notification {
    left: auto;
    right: 20px;
    width: 380px;
  }
}

.pwa-update-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.pwa-update-text {
  font-size: 15px;
  font-weight: 400;
  color: var(--text-primary);
  line-height: 1.5;
  margin: 0;
}

.pwa-update-buttons {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.pwa-update-btn {
  padding: 10px 20px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.pwa-update-btn.update {
  background: var(--btn-accent-bg);
  color: var(--btn-accent-text);
  border: 1px solid var(--btn-accent-bg);
}

.pwa-update-btn.update:hover {
  background: var(--btn-accent-hover);
  border-color: var(--btn-accent-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px var(--shadow-color);
}

.pwa-update-btn.later {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  border: 1px solid var(--border-medium);
}

.pwa-update-btn.later:hover {
  background: var(--border-light);
  color: var(--text-primary);
  border-color: var(--border-dark);
}

/* Анимации */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Темная тема */
html.dark .update-loader {
  background: var(--bg-secondary);
}

html.dark .pwa-update-notification {
  background: var(--bg-secondary);
  border-color: var(--border-medium);
}

html.dark .pwa-update-btn.later {
  background: var(--bg-tertiary);
  border-color: var(--border-dark);
}

html.dark .pwa-update-btn.later:hover {
  background: var(--border-dark);
}
</style>